import { apiGet, apiPut, apiPost, apiDelete } from '../api-client'

export type AdminShowStatus = 'pending' | 'published' | 'rejected'

export interface AdminShowSummary {
  id: string
  title: string
  creator: string
  submittedAt: string
  status: AdminShowStatus
  totalViews?: number
  episodeCount?: number
}

export interface AdminTopSeriesItem {
  id: string
  title: string
  creator: string
  totalViews: number
}

export interface AdminStats {
  pendingShows: number
  activeUsers: number
  newSignups7d: number
  hoursWatched7d: number
  approvedShows: number
  rejectedShows: number
  draftShows: number
  topSeries: AdminTopSeriesItem[]
}

interface RawAdminSeries {
  id?: string
  filmId?: string
  title?: string
  creatorId?: string
  creator?: { displayName?: string; username?: string }
  createdAt?: string
  status?: string
}

interface AdminUsersResponse {
  items: RawAdminUser[]
  totalCount?: number
}

interface RawAdminUser {
  userId: string
  email?: string
  username?: string
  displayName?: string
  isAdmin?: boolean
  isCreator?: boolean
  verified?: boolean
  createdAt?: string
}

export interface AdminUserItem {
  userId: string
  email: string
  displayName: string
  username?: string
  role: 'admin' | 'creator' | 'viewer'
  createdAt?: string
  isAdmin: boolean
  isCreator: boolean
  verified: boolean
}

export interface AdminUserDetail {
  userId: string
  email: string
  username?: string
  displayName?: string
  isAdmin: boolean
  isCreator: boolean
  verified: boolean
  createdAt?: string
  lastActiveAt?: string | null
  rewardPoints: number
  subscription: {
    status: string
    plan: string
    startedAt?: string
    renewsAt?: string
  } | null
}

export type AdminUserActivityEvent =
  | {
      type: 'view'
      occurredAt?: string
      filmId?: string
      filmTitle?: string
      episodeId?: string
      episodeTitle?: string
      episodeNumber?: number
      duration?: number
    }
  | {
      type: 'reward'
      occurredAt?: string
      amount?: number
      direction?: 'earned' | 'spent'
      reason?: string
      description?: string
    }

function mapSeriesToShow(row: RawAdminSeries): AdminShowSummary {
  const id = row.id || row.filmId || ''
  const title = row.title || 'Untitled series'
  const creator =
    row.creator?.displayName ||
    row.creator?.username ||
    row.creatorId ||
    'Unknown creator'
  const submittedAt = row.createdAt || ''
  let status: AdminShowStatus = 'pending'
  if (row.status === 'approved') status = 'published'
  else if (row.status === 'rejected') status = 'rejected'
  const totalViews =
    // backend may expose either totalViews or viewsCount on films
    (row as any).totalViews ?? (row as any).viewsCount ?? undefined
  const episodeCount = (row as any).episodeCount ?? undefined
  return { id, title, creator, submittedAt, status, totalViews, episodeCount }
}

export async function listAllShows(): Promise<AdminShowSummary[]> {
  const res = await apiGet<{ items: RawAdminSeries[] }>(
    '/admin/series/all?limit=200',
  )
  const items = Array.isArray(res.items) ? res.items : []
  return items.map(mapSeriesToShow)
}

export async function approveShow(id: string): Promise<boolean> {
  await apiPut(`/admin/series/${id}/approve`, {})
  return true
}

export async function rejectShow(id: string, reason?: string): Promise<boolean> {
  await apiPut(`/admin/series/${id}/reject`, reason != null ? { reason } : {})
  return true
}

// Admin series (film) detail and CRUD

export interface AdminSeriesDetail {
  filmId: string
  id: string
  title: string
  titleLower?: string
  synopsis?: string
  genre?: string
  subGenres?: string[]
  language?: string
  releaseYear?: number
  rating?: number
  episodeCount?: number
  coverImage?: string
  trailerUrl?: string | null
  monetization?: string
  price?: number | null
  isFeatured?: boolean
  isNew?: boolean
  creatorId?: string
  status?: string
  approvedAt?: string | null
  rejectionReason?: string | null
  createdAt?: string
  updatedAt?: string
  totalViews?: number
  episodes?: AdminEpisodeDetail[]
}

export interface AdminEpisodeDetail {
  episodeId: string
  filmId: string
  title: string
  description?: string
  episodeNumber: number
  season?: number
  videoUrl?: string
  streamKey?: string | null
  thumbnailUrl?: string
  duration?: number
  subtitles?: unknown[]
  isFree?: boolean
  status?: string
  createdAt?: string
  updatedAt?: string
}

export async function getAdminSeriesDetail(seriesId: string): Promise<AdminSeriesDetail> {
  const raw = await apiGet<Record<string, unknown>>(`/admin/series/${seriesId}`)
  const episodes = (raw.episodes as AdminEpisodeDetail[] | undefined) ?? []
  return {
    ...raw,
    filmId: (raw.filmId ?? raw.id) as string,
    id: (raw.id ?? raw.filmId) as string,
    episodes: episodes.sort((a, b) => (a.episodeNumber ?? 0) - (b.episodeNumber ?? 0)),
  } as AdminSeriesDetail
}

export async function updateAdminFilm(
  filmId: string,
  body: Partial<{
    title: string
    synopsis: string
    genre: string
    subGenres: string[]
    language: string
    releaseYear: number
    rating: number
    coverImage: string
    trailerUrl: string | null
    monetization: string
    price: number | null
    isFeatured: boolean
    isNew: boolean
    status: string
    rejectionReason: string | null
  }>,
): Promise<AdminSeriesDetail> {
  return apiPut<AdminSeriesDetail>(`/admin/films/${filmId}`, body)
}

export async function updateAdminEpisode(
  episodeId: string,
  body: Partial<{
    title: string
    description: string
    episodeNumber: number
    season: number
    videoUrl: string
    thumbnailUrl: string
    duration: number
    isFree: boolean
  }>,
): Promise<AdminEpisodeDetail> {
  return apiPut<AdminEpisodeDetail>(`/admin/episodes/${episodeId}`, body)
}

export async function createAdminEpisode(
  filmId: string,
  body: { title: string; description?: string; videoUrl?: string; thumbnailUrl?: string; duration?: number },
): Promise<AdminEpisodeDetail> {
  return apiPost<AdminEpisodeDetail>(`/admin/films/${filmId}/episodes`, body)
}

export async function deleteAdminEpisode(episodeId: string): Promise<void> {
  await apiDelete(`/admin/episodes/${episodeId}`)
}

export async function deleteAdminFilm(filmId: string): Promise<void> {
  await apiDelete(`/admin/films/${filmId}`)
}

export async function getAdminStats(): Promise<AdminStats> {
  const res = await apiGet<AdminStats>('/admin/overview')

  return {
    ...res,
    topSeries: Array.isArray(res.topSeries) ? res.topSeries : [],
  }
}

export async function listAdminUsers(): Promise<AdminUserItem[]> {
  const res = await apiGet<AdminUsersResponse>('/admin/users?limit=200')
  const items = Array.isArray(res.items) ? res.items : []
  return items.map((u) => {
    const isAdmin = !!u.isAdmin
    const isCreator = !!u.isCreator
    const role: AdminUserItem['role'] = isAdmin ? 'admin' : isCreator ? 'creator' : 'viewer'
    return {
      userId: u.userId,
      email: u.email || '',
      displayName: u.displayName || u.username || u.email || u.userId,
      username: u.username,
      role,
      createdAt: u.createdAt,
      isAdmin,
      isCreator,
      verified: !!u.verified,
    }
  })
}

export async function promoteUserToAdmin(userId: string): Promise<void> {
  await apiPut(`/admin/users/${userId}/promote`, { isAdmin: true })
}

export async function getAdminUserDetail(userId: string): Promise<AdminUserDetail> {
  return apiGet<AdminUserDetail>(`/admin/users/${userId}`)
}

export async function getAdminUserActivity(
  userId: string,
): Promise<{ items: AdminUserActivityEvent[]; totalCount: number }> {
  return apiGet<{ items: AdminUserActivityEvent[]; totalCount: number }>(
    `/admin/users/${userId}/activity`,
  )
}

// Support tickets

export type SupportTicketStatus = 'open' | 'pending' | 'closed'

export interface SupportTicket {
  id: string
  ticketId: string
  userId?: string | null
  email: string
  role?: string
  subject: string
  body?: string
  status: SupportTicketStatus
  createdAt: string
  updatedAt?: string
  lastAgent?: string | null
  tags?: string[]
}

export async function listSupportTickets(filters?: {
  status?: SupportTicketStatus
  limit?: number
}): Promise<{ items: SupportTicket[]; totalCount: number }> {
  const params = new URLSearchParams()
  if (filters?.limit) params.set('limit', String(filters.limit))
  if (filters?.status) params.set('status', filters.status)
  const qs = params.toString()
  const res = await apiGet<{ items: SupportTicket[]; totalCount?: number }>(
    `/admin/support-tickets${qs ? `?${qs}` : ''}`,
  )
  return {
    items: Array.isArray(res.items) ? res.items : [],
    totalCount: res.totalCount ?? (res.items?.length ?? 0),
  }
}

export async function getSupportTicket(ticketId: string): Promise<SupportTicket> {
  return apiGet<SupportTicket>(`/admin/support-tickets/${ticketId}`)
}

export async function updateSupportTicket(
  ticketId: string,
  payload: { status?: SupportTicketStatus; lastAgent?: string; internalNotes?: string },
): Promise<SupportTicket> {
  const res = await apiPut<SupportTicket>(`/admin/support-tickets/${ticketId}`, payload)
  return res
}

// Analytics

export interface AdminAnalyticsOverview {
  dailyActiveUsers: number
  newSignups7d: number
  hoursWatched7d: number
  creatorApprovalRate: number
}

export interface AdminSignupsSourcePoint {
  day: string
  app: number
  website: number
  social: number
  other: number
}

export interface AdminAnalyticsProductHealthPoint {
  day: string
  crashRate: number
  bufferingRate: number
  totalSessions: number
}

export interface AdminAnalyticsEngagementPoint {
  day: string
  dau: number
  hoursWatched: number
}

export interface AdminAnalyticsSubscriptionsFunnel {
  range: '7d' | '30d'
  funnel: {
    visits: number
    trialsStarted: number
    activeSubscriptions: number
  }
}

export interface AdminAnalyticsTopContentItem {
  id: string
  title: string
  creator: string
  totalViews: number
}

export interface SignupsByDayItem {
  day: string
  dayStart?: string
  viewers: number
  creators: number
}

export async function getAdminAnalyticsOverview(): Promise<AdminAnalyticsOverview> {
  return apiGet<AdminAnalyticsOverview>('/admin/analytics/overview')
}

export async function getAdminAnalyticsSignupsByDay(
  range?: '7d' | '14d',
): Promise<{ items: SignupsByDayItem[] }> {
  const qs = range ? `?range=${range}` : ''
  return apiGet<{ items: SignupsByDayItem[] }>(`/admin/analytics/signups-by-day${qs}`)
}

export async function getAdminAnalyticsProductHealth(
  range: '7d' | '30d',
): Promise<{ items: AdminAnalyticsProductHealthPoint[] }> {
  const qs = range ? `?range=${range}` : ''
  return apiGet<{ items: AdminAnalyticsProductHealthPoint[] }>(
    `/admin/analytics/product-health${qs}`,
  )
}

export async function getAdminAnalyticsEngagement(
  range: '7d' | '30d',
): Promise<{ items: AdminAnalyticsEngagementPoint[] }> {
  const qs = range ? `?range=${range}` : ''
  return apiGet<{ items: AdminAnalyticsEngagementPoint[] }>(`/admin/analytics/engagement${qs}`)
}

export async function getAdminAnalyticsSubscriptions(
  range: '7d' | '30d',
): Promise<AdminAnalyticsSubscriptionsFunnel> {
  const qs = range ? `?range=${range}` : ''
  return apiGet<AdminAnalyticsSubscriptionsFunnel>(`/admin/analytics/subscriptions${qs}`)
}

export async function getAdminAnalyticsTopContent(
  range: '7d' | '30d',
  limit = 10,
): Promise<{ range: '7d' | '30d'; items: AdminAnalyticsTopContentItem[] }> {
  const params = new URLSearchParams()
  if (range) params.set('range', range)
  if (limit) params.set('limit', String(limit))
  const qs = params.toString()
  return apiGet<{ range: '7d' | '30d'; items: AdminAnalyticsTopContentItem[] }>(
    `/admin/analytics/top-content${qs ? `?${qs}` : ''}`,
  )
}

export async function getAdminSignupsSources(
  range: '7d' | '30d' | '90d' = '30d',
): Promise<{ items: AdminSignupsSourcePoint[] }> {
  const params = new URLSearchParams()
  if (range) params.set('range', range)
  const qs = params.toString()
  return apiGet<{ items: AdminSignupsSourcePoint[] }>(
    `/admin/analytics/signups-sources${qs ? `?${qs}` : ''}`,
  )
}

